export const keywordResearch = {
  title: 'Keyword Research',
  subtitle: 'Generate keyword clusters and validate with real search data',
  input: {
    title: 'Start Your Research',
    description: 'Enter your company details and select your target market',
    companyName: { label: 'Company / Product Name', placeholder: 'e.g. HubSpot' },
    productUrl: { label: 'Product URL (optional)', placeholder: 'https://example.com' },
    companyDescription: {
      label: 'Company Description',
      placeholder: 'Describe what your company/product does, its main features, and target audience...',
      generateButton: 'Generate with AI',
      generating: 'Generating...',
    },
    country: { label: 'Target Country', placeholder: 'Select country' },
    language: { label: 'Language', placeholder: 'Select language' },
    startButton: 'Start Research',
  },
  steps: {
    rootProduct: {
      name: 'Root Product',
      fullName: 'Identify Root Product',
      description: 'AI analyzes your product to identify its core market categories',
      rootProductLabel: 'Root Product',
      categoriesLabel: 'Market Categories',
      editHint: 'Click on any category to edit or remove it',
      regenerateRootProduct: 'Regenerate root product',
      generateMoreCategories: 'Generate more categories',
      generating: 'Generating...',
      instructionsPlaceholder: 'Optional: describe what to change...',
      loadingTitle: 'Analyzing your product',
      loadingDescription: 'AI is identifying your core product and market categories. This usually takes about a minute.',
    },
    structuralClusters: {
      name: 'Clusters',
      fullName: 'Build Structural Clusters',
      description: 'Map the full market structure with keyword clusters A through J',
      clusterLabels: {
        A: 'Root Product',
        B: 'Platforms / Stacks',
        C: 'Features',
        D: 'Pains',
        E: 'Results / JTBD',
        F: 'Verticals / Audiences',
        G: 'Implementation / Dev',
        H: 'Connectors',
        J: 'Direct Competitors',
      },
      generateMoreKeywords: 'Generate more keywords',
      generating: 'Generating...',
      instructionsPlaceholder: 'Optional: describe what to focus on...',
      deleteCluster: 'Delete cluster',
      addCluster: 'Add cluster',
      newClusterNamePlaceholder: 'Cluster name...',
      loadingTitle: 'Building market clusters',
      loadingDescription: 'Mapping the full market structure with keyword clusters from your product categories. This usually takes a couple of minutes.',
    },
    mergeSystem: {
      name: 'Merges',
      fullName: 'Define Merge System',
      description: 'Generate keyword combination formulas for short, mid, and long tail',
      typeLabels: { short: 'Short Tail', mid: 'Mid Tail', long: 'Long Tail' },
      intentCategoryLabels: {
        purchase: 'Purchase',
        comparison: 'Comparison',
        feature: 'Feature',
        pain: 'Pain',
        outcome: 'Outcome',
        vertical: 'Vertical',
        implementation: 'Implementation',
        platform: 'Platform',
      },
      intentStrengthLabels: { high: 'High', medium: 'Medium', low: 'Low' },
      funnelStageLabels: { TOFU: 'TOFU', MOFU: 'MOFU', BOFU: 'BOFU' },
      columns: {
        intentCategory: 'Intent Category',
        type: 'Type',
        formula: 'Formula',
        formulaReadable: 'Formula (Readable)',
        description: 'Description',
        examples: 'Examples',
        intentStrength: 'Intent Strength',
        funnelStage: 'Funnel Stage',
        actions: 'Actions',
      },
      clearFilters: 'Clear filters',
      generateMore: 'Generate more merges',
      generating: 'Generating...',
      deleteConfirm: 'Remove merge',
      instructionsPlaceholder: 'Optional: describe what kind of merges to generate...',
      loadingTitle: 'Building your merge system',
      loadingDescription: 'Analyzing keyword clusters and generating combination formulas. This usually takes a couple of minutes.',
      filtering: 'Filtering...',
      noResults: 'No merges match the current filters.',
      allFilter: 'All',
    },
    keywordCombinations: {
      name: 'Keywords',
      fullName: 'Generate Keyword Combinations',
      description: 'System generates all keyword permutations from your clusters and merges',
      totalKeywords: '{{count}} keywords generated',
      removeHint: 'Click on a keyword to remove it from the list',
      searchPlaceholder: 'Search keywords...',
      showMore: 'Show {{remaining}} more',
      loadingTitle: 'Generating keyword combinations',
      loadingDescription: 'Creating all possible keyword permutations from your clusters and merge formulas. This usually takes a couple of minutes.',
    },
    agenticEnrichment: {
      name: 'AI Expand',
      fullName: 'AI Keyword Expansion',
      description: 'AI agent researches your market and generates thousands of additional keywords',
      startButton: 'Start AI Expansion',
      stopButton: 'Stop',
      rerun: 'Re-run Expansion',
      keywordsDiscovered: 'Keywords Discovered',
      activityLog: 'Activity Log',
      insights: 'Research Insights',
      researchTimeline: 'Research Timeline',
      seedKeywords: 'seeds found',
      batchesProgress: '{{completed}} of {{total}} batches complete',
      filterDedup: 'Removing duplicates',
      filterQuality: 'AI quality filter',
      completeSummary: 'new keywords added',
      phaseTime: '{{time}}s',
      errorTitle: 'Expansion failed',
      cancelled: 'Expansion was cancelled.',
      completeTitle: 'Expansion Complete',
      completedMessage: 'AI expansion added {{count}} new keywords.',
      loadingTitle: 'AI is researching your market',
      loadingDescription: 'The AI agent is analyzing competitors, trends, and user language to expand your keyword list.',
    },
    keywordExpansion: {
      name: 'Expand',
      fullName: 'Keyword Expansion',
      description: 'Discover new keywords from DataForSEO and AI-powered expansion',
      startButton: 'Start Expansion',
      stopButton: 'Stop',
      rerun: 'Re-run Expansion',
      keywordsDiscovered: 'New Keywords Discovered',
      phases: {
        discovery: 'DataForSEO Discovery',
        aiExpansion: 'AI Expansion',
        volumeEnrichment: 'Volume Enrichment',
      },
      batches: {
        site: 'Site Keywords',
        keywords: 'Keyword Suggestions',
        ai: 'AI Expansion',
      },
      stats: {
        fromSite: 'from site scan',
        fromKeywords: 'from keyword suggestions',
        fromAI: 'from AI expansion',
      },
      completeSummary: 'new keywords discovered',
      phaseTime: '{{time}}s',
      errorTitle: 'Expansion failed',
      cancelled: 'Expansion was cancelled.',
      completeTitle: 'Expansion Complete',
      completedMessage: 'Keyword expansion discovered {{count}} new keywords.',
    },
    enrichment: {
      name: 'Enrichment',
      fullName: 'Search Volume Enrichment',
      description: 'Enrich your keywords with real search volume data and review intent tags',
      searchVolume: 'Search Volume',
      competition: 'Competition',
      cpc: 'CPC',
      keyword: 'Keyword',
      actions: 'Actions',
      intentCategory: 'Intent',
      intentStrength: 'Strength',
      funnelStage: 'Funnel',
      noData: '—',
      noTrendData: 'No trend data available for this keyword',
      loadingTrend: 'Loading trend data...',
      trendError: 'Could not load trend data',
      avgMonthly: 'Avg: {{value}}/mo',
      fetchingVolumes: 'Fetching search volumes...',
      volumeError: 'Could not fetch search volumes',
      retryButton: 'Retry',
      filterPlaceholder: 'Filter keywords...',
      exportCsv: 'Download CSV',
      costWarning: 'Each refresh uses an API credit. Avoid refreshing frequently.',
      refreshVolumes: 'Refresh',
      staleWarning: 'Keywords have changed since last volume check',
      loadingTitle: 'Enriching keywords',
      loadingDescription: 'Fetching search volume and competition data from Google. This usually takes a couple of minutes.',
    },
    validation: {
      name: 'Validate',
      fullName: 'Validate with Real Data',
      description: 'Fetch real search volume and competition data from Google Keyword Planner',
      fetchingProgress: 'Fetching data for {{count}} keywords...',
      showMore: 'Show {{remaining}} more',
      downloadCsv: 'Download CSV',
      searchPlaceholder: 'Search validated keywords...',
      loadingTitle: 'Validating with Google',
      loadingDescription: 'Fetching real search volume and competition data from Google Keyword Planner. This usually takes a couple of minutes.',
      columns: {
        keyword: 'Keyword',
        search_volume: 'Search Volume',
        competition: 'Competition',
        competition_index: 'Competition Index',
        cpc: 'CPC',
        low_top_of_page_bid: 'Low Bid',
        high_top_of_page_bid: 'High Bid',
        funnelStage: 'Funnel Stage',
        monetizable: 'Monetizable',
      },
    },
  },
  navigation: {
    previous: 'Previous',
    next: 'Next',
    regenerate: 'Re-generate',
    skip: 'Skip',
  },
  completion: {
    title: 'Research Complete!',
    subtitle: 'Your keyword research is ready for download',
    downloadButton: 'Download CSV',
    startNew: 'Start New Research',
  },
  loading: {
    analyzingProduct: 'Analyzing product...',
    buildingClusters: 'Building market clusters...',
    definingMerges: 'Defining merge system...',
    generatingKeywords: 'Generating keyword combinations...',
    validatingKeywords: 'Validating keywords with Google...',
    exportingCsv: 'Generating CSV...',
  },
  errors: {
    rootProductFailed: 'Failed to analyze product: {{message}}',
    clustersFailed: 'Failed to build clusters: {{message}}',
    mergeFailed: 'Failed to define merges: {{message}}',
    combinationsFailed: 'Failed to generate combinations: {{message}}',
    validationFailed: 'Failed to validate keywords: {{message}}',
    exportFailed: 'Failed to export CSV: {{message}}',
    retryButton: 'Retry',
    manualInput: 'Enter manually',
  },
  agenticKeyword: {
    title: 'AI Keyword Expansion',
    subtitle: 'Let AI research your market and discover thousands of keywords',
    steps: {
      input: 'Company Info',
      expansion: 'AI Expansion',
      results: 'Results & Export',
    },
    analyzingProduct: 'Analyzing your product...',
    analyzingDescription: 'AI is identifying your core product and market categories.',
    analyzeError: 'Failed to analyze product',
  },
  list: {
    title: 'Keyword Researches',
    subtitle: 'Manage your saved keyword research results',
    searchPlaceholder: 'Search by name...',
    newResearch: 'New Research',
    standardAlgorithm: 'Standard Algorithm',
    aiExpansion: 'AI Expansion',
    emptyTitle: 'No keyword researches yet',
    emptyDescription: 'Start a new keyword research to discover and save keywords for your content strategy.',
    deleteTitle: 'Delete Keyword Research',
    deleteDescription: 'Are you sure you want to delete "{{name}}"? This will remove all associated keywords. This action cannot be undone.',
    columns: {
      name: 'Name',
      description: 'Description',
      country: 'Country',
      language: 'Language',
      status: 'Status',
      keywords: 'Keywords',
      created: 'Created',
    },
    paginationResults: '{{count}} results',
    cancel: 'Cancel',
    deleteSuccess: 'Keyword research deleted',
    deleteError: 'Failed to delete keyword research',
  },
  detail: {
    backToList: 'Back to Keyword Researches',
    exportCsv: 'Export CSV',
    linkedinEnrich: 'Enrich with LinkedIn',
    expandKeywords: 'Expand Keywords',
    searchPlaceholder: 'Search keywords...',
    editName: 'Edit name',
    notFound: 'Keyword research not found',
    saveName: 'Save',
    cancelEdit: 'Cancel',
    updateNameError: 'Failed to update name',
    keywordsLoadError: 'Failed to load keywords',
    retryLoad: 'Retry',
    noKeywords: 'No keywords yet',
    noKeywordsDescription: 'Save keywords from a research wizard to see them here.',
    columns: {
      keyword: 'Keyword',
      searchVolume: 'Search Volume',
      competition: 'Competition',
      competitionIndex: 'Competition Index',
      cpc: 'CPC',
      lowBid: 'Low Bid',
      highBid: 'High Bid',
      linkedinPosts: 'LI Posts',
      intentCategory: 'Intent',
      intentStrength: 'Strength',
      funnelStage: 'Funnel',
      source: 'Source',
    },
    filters: {
      minSearchVolume: 'Min Volume',
      maxCpc: 'Max CPC',
      intentCategory: 'Intent Category',
      intentStrength: 'Intent Strength',
      funnelStage: 'Funnel Stage',
      all: 'All',
      clearFilters: 'Clear filters',
      hasLinkedinPosts: 'LinkedIn Posts',
      withLinkedinPosts: 'With linked posts',
      source: 'Source',
    },
    sourceLabels: {
      googleTrends: 'Trends',
    },
    linkedinPopover: {
      title: 'Linked Posts ({{count}})',
      noPostsFound: 'No posts found',
    },
    expandDialog: {
      title: 'Expand Keywords',
      description: 'Discover new keywords by scanning your site, generating suggestions, and using AI expansion.',
      productUrlLabel: 'Product URL',
      productUrlPlaceholder: 'https://example.com',
      rootProductLabel: 'Root Product (optional)',
      rootProductPlaceholder: 'e.g. CRM Software',
      companyDescriptionLabel: 'Company Description (optional)',
      companyDescriptionPlaceholder: 'Describe what your company/product does...',
      startButton: 'Start Expansion',
    },
    selectAll: 'Select all keywords',
    selectKeyword: 'Select keyword',
    selectedCount: '{{count}} keywords selected',
    planContents: 'Plan contents',
    clearSelection: 'Clear selection',
    loadingMore: 'Loading more...',
    keywordCountProgress: '{{loaded}} / {{total}} keywords',
    allLoaded: '(all loaded)',
    paginationInfo: '{{from}}-{{to}} of {{total}} keywords',
    expandPanel: {
      dismiss: 'Dismiss',
      restart: 'Restart Expansion',
      expansionFailed: 'Expansion failed',
    },
  },
  save: {
    buttonLabel: 'Save Keywords',
    dialogTitle: 'Save Keywords',
    dialogDescription: 'Save {{count}} keywords to a keyword research.',
    createNew: 'Create New',
    addToExisting: 'Add to Existing',
    nameLabel: 'Name',
    namePlaceholder: 'e.g. Q1 2026 SEO Keywords',
    descriptionLabel: 'Description (optional)',
    descriptionPlaceholder: 'Brief description of this research...',
    countryLabel: 'Country',
    languageLabel: 'Language',
    selectResearch: 'Select a keyword research',
    noExistingResearches: 'No existing keyword researches found',
    saving: 'Saving...',
    saveButton: 'Save',
    successMessage: 'Keywords saved successfully',
    errorMessage: 'Failed to save keywords',
  },
  creation: {
    title: 'New Keyword Research',
    subtitle: 'Configure your research and our AI agent will take care of the rest',
    modes: {
      ai: 'Generate with AI',
      import: 'Import CSV',
    },
    fields: {
      name: { label: 'Research Name', placeholder: 'e.g. Q1 2026 SEO Keywords' },
      companyName: { label: 'Company / Product Name', placeholder: 'e.g. HubSpot' },
      productUrl: {
        label: 'Product URL',
        placeholder: 'https://example.com',
        hint: 'Include the full domain (e.g. https://yoursite.com)',
      },
      companyDescription: {
        label: 'Company Description',
        placeholder: 'Describe what your company/product does, its main features, and target audience...',
      },
      country: { label: 'Target Country', placeholder: 'Select country' },
      language: { label: 'Language', placeholder: 'Select language' },
    },
    description: {
      badge: 'Most Important',
      helper: 'This description drives the entire AI research. The more detailed and accurate, the better the results.',
      generateButton: 'Generate with AI',
      generating: 'Generating...',
      aiWarning: 'AI-generated descriptions may not fully reflect your product. Review and edit before submitting.',
    },
    asyncBanner: 'Background process — estimated ~30 minutes',
    submit: {
      button: 'Start Research',
      submitting: 'Starting...',
    },
    validation: {
      toastTitle: 'Check the form for missing details',
      toastFallback: 'Some fields need your attention.',
      nameRequired: 'Give this research a name.',
      companyNameRequired: 'Enter your company or product name.',
      productUrlRequired: 'Enter your product URL.',
      productUrlInvalid: 'That URL is not valid. Use the format https://yoursite.com',
      countryRequired: 'Pick a target country.',
      languageRequired: 'Pick a language.',
      submitFailed: "We couldn't start the research",
      generateDescriptionFailed: "We couldn't generate the description",
    },
    confirmation: {
      title: 'Research Started!',
      subtitle: 'Your research is running in the background',
      summary: {
        name: 'Name',
        company: 'Company',
        url: 'URL',
        country: 'Country',
        language: 'Language',
      },
      estimatedTime: 'Estimated time: ~30 minutes',
      trackProgress: 'You can track progress in your research list',
      goToList: 'Go to My Researches',
      startAnother: 'Start Another Research',
    },
    import: {
      title: 'Import Keyword Research from CSV',
      subtitle: 'Upload a CSV, map its columns, and save the keywords directly to a research',
      descriptionLabel: 'Description (optional)',
      descriptionPlaceholder: 'Short note about this imported dataset...',
      uploadTitle: 'CSV Upload',
      uploadHint: 'Upload the CSV you want to import. We will detect headers and let you map each column.',
      previewLoading: 'Reading CSV and preparing preview...',
      previewError: 'Could not preview the CSV',
      fileSummary: '{{name}} loaded with {{rows}} rows and {{columns}} columns',
      mappingTitle: 'Column Mapping',
      mappingHint: 'Choose which CSV column should fill each keyword field. Only the keyword column is required.',
      selectColumn: 'Select CSV column',
      unmapped: 'Do not import',
      required: '(required)',
      previewTitle: 'CSV Preview',
      previewHint: 'Showing the first rows exactly as they were parsed from the file.',
      reparse: 'Refresh Preview',
      reset: 'Reset',
      submit: 'Import Research',
      submitting: 'Importing...',
      submitError: 'Failed to import CSV',
      success: 'Imported {{count}} keywords ({{skipped}} skipped)',
      dropzoneTitle: 'Drag and drop your CSV here',
      dropzoneOr: 'or',
      autoMapped: 'Auto',
      conflictBadge: 'Conflict',
      conflictWithMessage: 'This column is already assigned to "{{field}}". Pick a different column or unset it from the other field.',
      duplicateMappingBannerTitle: 'Some columns are assigned to more than one field',
      duplicateMappingBannerHint: 'Each CSV column can only map to one target field. Resolve the conflicts before importing.',
      usedBy: 'used by {{field}}',
      rows: '{{count}} rows',
      columns: '{{count}} cols',
      fields: {
        keyword: 'Keyword',
        search_volume: 'Search Volume',
        competition: 'Competition',
        competition_index: 'Competition Index',
        cpc: 'CPC',
        low_top_of_page_bid: 'Low Top of Page Bid',
        high_top_of_page_bid: 'High Top of Page Bid',
        intent_category: 'Intent Category',
        intent_strength: 'Intent Strength',
        funnel_stage: 'Funnel Stage',
      },
    },
  },
  toast: { exportSuccess: 'CSV downloaded successfully' },
  countries: {
    GLOBAL: 'Global',
    US: 'United States', ES: 'Spain', AR: 'Argentina', MX: 'Mexico',
    CO: 'Colombia', CL: 'Chile', PE: 'Peru', GT: 'Guatemala', BR: 'Brazil',
    GB: 'United Kingdom', DE: 'Germany', FR: 'France',
  },
  languages: { en: 'English', es: 'Spanish', pt: 'Portuguese', fr: 'French', de: 'German' },
  asyncPipeline: {
    runInBackground: 'Run in background',
    researchName: 'Research Name',
    researchNamePlaceholder: 'e.g. Q1 2026 SEO Keywords',
    startAsync: 'Start Background Research',
    asyncStarted: 'Background research started! You can close this page.',
    asyncStartError: 'Failed to start background research',
    stepNames: {
      root_product: 'Analyzing Product',
      structural_clusters: 'Building Clusters',
      merge_system: 'Creating Merges',
      keyword_combinations: 'Generating Keywords',
      agentic_enrichment: 'AI Expansion',
      search_volume: 'Fetching Volumes',
      keyword_expansion: 'Expanding Keywords',
      saving_keywords: 'Saving Keywords',
    },
    jobStatus: {
      pending: 'Pending',
      running: 'Running',
      completed: 'Completed',
      failed: 'Failed',
    },
  },
  collections: {
    selectFolder: 'Select all keywords in {{name}}',
    ungroupedFolder: 'Unassigned',
    newFolder: 'New folder',
    nameLabel: 'Folder name',
    namePlaceholder: 'e.g. Winter campaign',
    createTitle: 'New folder',
    createEmpty: 'Create an empty folder. You can add keywords to it later.',
    createWithSelection: '{{count}} selected keyword will be added to the new folder.',
    createWithSelection_one: '{{count}} selected keyword will be added to the new folder.',
    createWithSelection_other: '{{count}} selected keywords will be added to the new folder.',
    createConfirm: 'Create folder',
    addToFolder: 'Add to folder',
    addToFolderMenuTitle: 'Add to an existing folder',
    moveToFolder: 'Move to folder',
    moveToFolderMenuTitle: 'Move to a folder (removes from others)',
    noFolders: 'No folders yet',
    loadingFolders: 'Loading folders…',
    folderMenu: 'Folder options for {{name}}',
    rename: 'Rename',
    delete: 'Delete',
    renameTitle: 'Rename folder',
    renameConfirm: 'Save',
    renamed: 'Folder renamed',
    deleteTitle: 'Delete folder',
    deleteConfirmBody:
      'Delete "{{name}}"? Its keywords stay in your research and in any other folder they belong to.',
    removeFromFolder: 'Remove from folder',
    removeFromFolderMenuTitle: 'Remove selection from a folder',
    keywordsRemoved: '{{count}} keyword removed from the folder',
    keywordsRemoved_one: '{{count}} keyword removed from the folder',
    keywordsRemoved_other: '{{count}} keywords removed from the folder',
    keywordsMoved: '{{count}} keyword moved',
    keywordsMoved_one: '{{count}} keyword moved',
    keywordsMoved_other: '{{count}} keywords moved',
  },
  selection: {
    keywordCount_one: '{{count}} keyword',
    keywordCount_other: '{{count}} keywords',
    keywordCount: '{{count}} keywords',
    collectionCreated: 'Folder created',
    collectionDeleted: 'Folder deleted',
    keywordsAdded: '{{count}} keywords added',
    keywordsAdded_one: '{{count}} keyword added',
    keywordsAdded_other: '{{count}} keywords added',
    totalVolume: 'Vol: {{value}}',
    avgCpc: 'CPC: {{value}}',
    bucketTransactional: '{{count}} transactional',
    bucketInformational: '{{count}} informational',
    addManualButton: 'Add keyword',
    addManualTitle: 'Add manual keyword',
    addManualDescription: 'Add a single keyword. Volume + CPC are enriched in the background if not available immediately.',
    addManualConfirm: 'Add',
    keywordLabel: 'Keyword',
    keywordPlaceholder: 'best crm for ecommerce',
    intentCategoryLabel: 'Intent',
    intentStrengthLabel: 'Strength',
    funnelStageLabel: 'Funnel',
    manualAddedEnriched: 'Keyword added and enriched',
    manualAddedQueued: 'Keyword added, enrichment queued',
    errorGeneric: 'Something went wrong',
    positioning: {
      button: 'Add to positioning list',
      menuTitle: 'Add to a positioning list',
      createNew: 'Create new list…',
      noLists: 'No lists yet',
      loading: 'Loading lists…',
      added: '{{count}} keywords added',
      added_one: '{{count}} keyword added',
      added_other: '{{count}} keywords added',
      addedWithSkipped: '{{added}} added, {{skipped}} already existed',
      createdAndAdded: 'List created · {{count}} keywords added',
      createdAndAdded_one: 'List created · {{count}} keyword added',
      createdAndAdded_other: 'List created · {{count}} keywords added',
      createdAndAddedWithSkipped: 'List created · {{added}} added, {{skipped}} already existed',
      error: 'Could not add keywords to the list',
    },
  },
} as const
